import type { GameState } from '../state'
import { SAVE_KEYS } from '../state'
import { serializeFlags, deserializeFlags, createFlags } from '../flags'
import type { StoragePort } from './pipeline'

// The v4 save format (step 7b, user decisions booked in SEPARATION_PLAN §4.7):
// - 43 derived keys stop being persisted (recomputed by computeDerived on
//   load); lastUpdate stops too (always overwritten at load).
// - backgroundPatternOn + confirmations move to the dodecaPrefs store (own
//   version + migration chain); achievementFlashActive / currentTab /
//   nuclearPastaStateInfo simply stop being persisted (loadGame force-reset
//   the first two on every load anyway; the third is a hover tooltip).
// - a `flags` array joins the save (endingReached-first ordering; empty until
//   step 13 sets the first one).
// - legacy adopted keys stop being persisted (the runtime keeps them for the
//   session; a v4 blob contains only known keys).
// - the RUNTIME game object keeps all 237 fields — only the blob shrinks.

export const CURRENT_SAVE_VERSION = 4

type Loose = GameState & Record<string, any>

/** The 42 Tier-A derived keys + lastUpdate (see docs — conservative list). */
export const V4_DROPPED_KEYS: readonly string[] = [
  'lastUpdate',
  'goldPerSecond', 'goldPerClick', 'firePerSecond', 'fireGoldMultiplier',
  'platinumToGet', 'magicToGet', 'magicEffect', 'magicScoreToGet',
  'uraniumToGet', 'plutoniumToGet',
  'cyanSigilsToGet', 'cyanSigilPowerPerSecond',
  'blueSigilsToGet', 'blueSigilPowerPerSecond',
  'indigoSigilsToGet', 'indigoSigilPowerPerSecond',
  'violetSigilsToGet', 'violetSigilPowerPerSecond',
  'pinkSigilsToGet', 'pinkSigilPowerPerSecond',
  'redSigilsToGet', 'redSigilPowerPerSecond',
  'orangeSigilsToGet', 'orangeSigilPowerPerSecond',
  'yellowSigilsToGet', 'yellowSigilPowerPerSecond',
  'knowledgeTradeLevelCap',
  'blueFirePerSecond', 'bloodPerSecond', 'holyFirePerSecond',
  'holyTetrahedronsToGet', 'holyOctahedronsToGet', 'holyDodecahedronsToGet',
  'planetEffect', 'cosmicPlaguePerSecond', 'oganessonPerSecond',
  'lightEssencePerSecond', 'darkEssencePerSecond', 'deathEssencePerSecond',
  'finalityEssencePerSecond', 'finalityCubeEffect',
  'dragonTimeEffect',
]

/** Lifted to the dodecaPrefs store. */
export const V4_PREF_KEYS: readonly string[] = ['backgroundPatternOn', 'confirmations']

/** Dropped outright (transient presentation; load force-reset them anyway). */
export const V4_TRANSIENT_KEYS: readonly string[] = ['achievementFlashActive', 'currentTab', 'nuclearPastaStateInfo']

const OMITTED = new Set<string>([...V4_DROPPED_KEYS, ...V4_PREF_KEYS, ...V4_TRANSIENT_KEYS])

/** The persisted flag set for this session (loaded from the blob; step 13 writes the first flag). */
export const gameFlags = createFlags()

/** v4 writer: SAVE_KEYS order minus omissions, plus version + flags. */
export function serializeV4(game: Loose): string {
  const out: Record<string, unknown> = {}
  for (const key of SAVE_KEYS) {
    if (OMITTED.has(key)) continue
    if (key === 'lastMajorChangeVersion') { out[key] = CURRENT_SAVE_VERSION; continue }
    out[key] = game[key]
  }
  out.flags = serializeFlags(gameFlags)
  return JSON.stringify(out)
}

export const PREFS_KEY = 'dodecaPrefs'
export const V3_BACKUP_KEY = 'dodecaSaveV3Backup'

export interface Prefs {
  v: number
  backgroundPatternOn: boolean
  confirmations: boolean[]
}

export function readPrefs(storage: StoragePort): Prefs | null {
  const raw = storage.get(PREFS_KEY)
  if (raw === null) return null
  try {
    const p = JSON.parse(raw)
    if (typeof p === 'object' && p !== null && typeof p.v === 'number') return p as Prefs
  } catch { /* fall through */ }
  return null
}

export function writePrefs(game: Loose, storage: StoragePort): void {
  const prefs: Prefs = {
    v: 1,
    backgroundPatternOn: game.backgroundPatternOn,
    confirmations: game.confirmations,
  }
  storage.set(PREFS_KEY, JSON.stringify(prefs))
}

/** Overlay stored prefs onto the runtime fields (after merge, before replay). */
export function overlayPrefs(game: Loose, storage: StoragePort): void {
  const p = readPrefs(storage)
  if (!p) return
  if (typeof p.backgroundPatternOn === 'boolean') game.backgroundPatternOn = p.backgroundPatternOn
  if (Array.isArray(p.confirmations)) game.confirmations = p.confirmations
}

/** One-time verbatim backup of the pre-v4 blob (never overwritten). */
export function backupPreV4(raw: string, storage: StoragePort): void {
  if (storage.get(V3_BACKUP_KEY) === null) storage.set(V3_BACKUP_KEY, raw)
}

/**
 * Seed the prefs store ONCE from the MERGED game values (after the merge's
 * element-wise default-tail semantics and the :554 confirmations repair have
 * run — seeding from the raw blob would truncate short legacy arrays).
 */
export function seedPrefsFromGame(game: Loose, storage: StoragePort): void {
  if (storage.get(PREFS_KEY) === null) writePrefs(game, storage)
}

/** Restore the session flag set from a blob's flags array (absent = empty). */
export function loadFlags(loadgame: Record<string, any>): void {
  const restored = deserializeFlags(loadgame.flags)
  gameFlags._set.clear()
  for (const f of restored._set) gameFlags._set.add(f)
}
